import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BarChart3, Boxes, KeyRound, Truck, type LucideIcon } from 'lucide-react';
import { APP_TAGLINE, loginSchema, type LoginInput } from '@amzjp/shared';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardBody } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { MarkaYazisi, UlkeRozeti } from '@/components/layout/marka';
import { ApiError } from '@/lib/api';
import { pazar } from '@/lib/pazar';
import { useAuth } from './auth-context';

const OZELLIKLER: Array<{ simge: LucideIcon; baslik: string; metin: string }> = [
  {
    simge: Boxes,
    baslik: 'Envanter ve fiyat',
    metin: 'Tüm ürünleriniz tek ekranda, fiyatlar kâr kurallarınıza göre.',
  },
  {
    simge: Truck,
    baslik: 'Sipariş ve kargo',
    metin: 'Bekleyen siparişler, kargo bildirimi ve geciken gönderimler.',
  },
  {
    simge: BarChart3,
    baslik: 'Kâr ve raporlar',
    metin: 'Sipariş bazında kâr, satış ve mağaza sağlığı raporları.',
  },
];

/** Geniş ekranda sol yarı. Logonun renklerinde, iki temada da aynı. */
function MarkaPaneli() {
  const p = pazar();
  return (
    <aside
      className="relative hidden w-[42%] max-w-[38rem] flex-col justify-between overflow-hidden p-12 text-white lg:flex"
      style={{
        backgroundImage:
          'linear-gradient(160deg, var(--marka-lacivert) 0%, var(--marka-lacivert-2) 100%)',
      }}
    >
      <div className="flex items-center gap-3">
        <img
          src="/logo.svg"
          alt=""
          className="size-12 rounded-[var(--radius)]"
          aria-hidden="true"
        />
        <div>
          <p className="text-lg font-bold leading-5 tracking-[-0.01em]">KSKLC</p>
          <p className="text-[0.6875rem] font-semibold uppercase leading-4 tracking-[0.12em] text-white/60">
            Amazon Seller App
          </p>
        </div>
      </div>

      <div className="space-y-7">
        <div className="space-y-3">
          <span className="inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-white/15 bg-white/5 px-2.5 py-1 text-xs font-semibold">
            <span aria-hidden="true">{p.bayrak}</span>
            {p.ad} paneli
          </span>
          <p className="max-w-sm text-2xl font-semibold leading-8 tracking-[-0.02em]">
            {APP_TAGLINE}
          </p>
        </div>
        <ul className="space-y-4">
          {OZELLIKLER.map(({ simge: Simge, baslik, metin }) => (
            <li key={baslik} className="flex gap-3">
              <span
                className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-[var(--radius-sm)] bg-white/5"
                style={{ color: 'var(--marka-altin)' }}
                aria-hidden="true"
              >
                <Simge className="size-4" />
              </span>
              <div>
                <p className="text-sm font-semibold">{baslik}</p>
                <p className="text-[0.8125rem] leading-5 text-white/60">{metin}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <p className="text-xs text-white/45">Bu panel yalnızca yetkili kullanıcılar içindir.</p>
    </aside>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const [formError, setFormError] = useState<string | null>(null);
  const [needsTotp, setNeedsTotp] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await login(values);
    } catch (error) {
      if (error instanceof ApiError) {
        // 2FA açıksa sunucu önce kod ister — formu genişletip tekrar denetiriz
        if (error.code === 'TOTP_REQUIRED') {
          setNeedsTotp(true);
          setFormError(error.message);
          return;
        }
        setFormError(error.message);
        return;
      }
      setFormError('Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.');
    }
  });

  return (
    <div className="flex min-h-screen bg-bg">
      <MarkaPaneli />

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm space-y-6">
          {/* Dar ekranda marka paneli yok: ad ve ülke formun üstünde */}
          <div className="flex items-center justify-center gap-2.5 lg:hidden">
            <img
              src="/logo.svg"
              alt=""
              className="size-11 rounded-[var(--radius)]"
              aria-hidden="true"
            />
            <MarkaYazisi />
            <UlkeRozeti uzun />
          </div>

          <div className="space-y-1 max-lg:text-center">
            <h1 className="text-xl font-semibold tracking-[-0.02em] text-fg">Giriş yapın</h1>
            <p className="text-[0.8125rem] text-fg-muted">
              Panele erişmek için hesap bilgilerinizi girin.
            </p>
          </div>

          <Card>
            <CardBody>
              <form onSubmit={onSubmit} className="space-y-4" noValidate>
                {formError && <Alert tone="danger" title={formError} />}

                <Field label="E-posta" htmlFor="email" error={errors.email?.message} required>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    autoFocus
                    aria-invalid={errors.email ? true : undefined}
                    {...register('email')}
                  />
                </Field>

                <Field label="Şifre" htmlFor="password" error={errors.password?.message} required>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    aria-invalid={errors.password ? true : undefined}
                    {...register('password')}
                  />
                </Field>

                {needsTotp && (
                  <Field
                    label="Doğrulama kodu"
                    htmlFor="totpCode"
                    hint="Kimlik doğrulama uygulamanızdaki 6 haneli kod."
                    error={errors.totpCode?.message}
                    required
                  >
                    <Input
                      id="totpCode"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="000000"
                      className="font-code tracking-[0.4em]"
                      aria-invalid={errors.totpCode ? true : undefined}
                      {...register('totpCode')}
                    />
                  </Field>
                )}

                <Button type="submit" className="w-full" loading={isSubmitting}>
                  <KeyRound aria-hidden="true" />
                  Giriş yap
                </Button>
              </form>
            </CardBody>
          </Card>

          <p className="text-center text-xs text-fg-subtle lg:hidden">
            Bu panel yalnızca yetkili kullanıcılar içindir.
          </p>
        </div>
      </main>
    </div>
  );
}
